<template>
  <div class="audit-page">
    <div class="heading">
      <div>
        <h1>审计日志</h1>
        <p>追踪管理员操作与系统自动任务，敏感字段已脱敏。</p>
      </div>
      <a-button :loading="loading" @click="load">刷新</a-button>
    </div>

    <a-card size="small" class="filter-card">
      <div class="filters">
        <a-select
          v-model:value="filters.action"
          allow-clear
          show-search
          option-filter-prop="label"
          :options="actionOptions"
          placeholder="操作类型"
          style="min-width:200px"
        />
        <a-input
          v-model:value="filters.keyword"
          allow-clear
          placeholder="搜索节点 ID、服务器 ID 或详情"
          style="min-width:260px"
          @press-enter="applyFilters"
        />
        <input v-model="filters.from" class="date-input" type="date" aria-label="开始日期" />
        <span class="date-separator">至</span>
        <input v-model="filters.to" class="date-input" type="date" aria-label="结束日期" />
        <a-button type="primary" @click="applyFilters">查询</a-button>
        <a-button @click="resetFilters">重置</a-button>
      </div>
    </a-card>

    <div class="summary-row">
      <a-card size="small"><a-statistic title="匹配日志" :value="total" /></a-card>
      <a-card size="small"><a-statistic title="本页记录" :value="items.length" /></a-card>
      <a-card size="small"><a-statistic title="操作类型" :value="actions.length" /></a-card>
    </div>

    <a-card class="desktop-table-card" :body-style="{ padding: 0 }">
      <a-table
        :data-source="items"
        :columns="columns"
        :loading="loading"
        :pagination="false"
        row-key="id"
        :scroll="{ x: 900 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'created_at'">{{ formatDateTime(record.created_at) }}</template>
          <template v-else-if="column.key === 'action'">
            <a-tag :color="auditActionColor(record.action)">{{ auditActionLabel(record.action) }}</a-tag>
            <div class="action-code">{{ record.action }}</div>
          </template>
          <template v-else-if="column.key === 'actor'">{{ auditActor(record) }}</template>
          <template v-else-if="column.key === 'summary'">{{ describeAuditLog(record) }}</template>
          <template v-else-if="column.key === 'details'">
            <a-button type="link" size="small" @click="openDetail(record)">查看详情</a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <div class="mobile-list">
      <a-spin :spinning="loading">
        <a-card v-for="item in items" :key="item.id" size="small" class="audit-card" @click="openDetail(item)">
          <div class="audit-card-head">
            <a-tag :color="auditActionColor(item.action)">{{ auditActionLabel(item.action) }}</a-tag>
            <span>{{ formatDateTime(item.created_at) }}</span>
          </div>
          <strong>{{ describeAuditLog(item) }}</strong>
          <div class="audit-card-foot">{{ auditActor(item) }} · #{{ item.id }}</div>
        </a-card>
        <a-empty v-if="!loading && items.length === 0" description="暂无审计日志" />
      </a-spin>
    </div>

    <div class="pagination-wrap">
      <a-pagination
        v-model:current="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        :page-size-options="['20', '50', '100', '200']"
        show-size-changer
        :show-total="(value: number) => `共 ${value} 条`"
        @change="onPageChange"
        @show-size-change="onPageSizeChange"
      />
    </div>

    <a-drawer
      :open="detailOpen"
      :title="selected ? `${auditActionLabel(selected.action)} · #${selected.id}` : '日志详情'"
      width="680"
      @close="detailOpen = false"
    >
      <template v-if="selected">
        <a-descriptions :column="1" bordered size="small" style="margin-bottom:16px">
          <a-descriptions-item label="操作时间">{{ formatDateTime(selected.created_at) }}</a-descriptions-item>
          <a-descriptions-item label="操作类型">{{ auditActionLabel(selected.action) }}（{{ selected.action }}）</a-descriptions-item>
          <a-descriptions-item label="操作人">{{ auditActor(selected) }}</a-descriptions-item>
          <a-descriptions-item label="操作摘要">{{ describeAuditLog(selected) }}</a-descriptions-item>
        </a-descriptions>
        <a-alert type="info" show-icon message="敏感字段由服务器统一脱敏后返回。" style="margin-bottom:12px" />
        <pre class="payload-json">{{ JSON.stringify(selected.payload, null, 2) }}</pre>
      </template>
    </a-drawer>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import { message } from 'ant-design-vue';
import { listAuditLogs, type AuditLogItem } from '../api/auditLogs';
import { auditActionColor, auditActionLabel, auditActor, describeAuditLog } from '../components/auditLogPresentation';

const items = ref<AuditLogItem[]>([]);
const actions = ref<string[]>([]);
const total = ref(0);
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(50);
const detailOpen = ref(false);
const selected = ref<AuditLogItem | null>(null);
const filters = reactive({ action: undefined as string | undefined, keyword: '', from: '', to: '' });

const columns = [
  { title: '时间', key: 'created_at', width: 180 },
  { title: '操作类型', key: 'action', width: 210 },
  { title: '操作人', key: 'actor', width: 110 },
  { title: '操作摘要', key: 'summary' },
  { title: '详情', key: 'details', width: 90 },
];

const actionOptions = computed(() => actions.value.map((action) => ({
  value: action,
  label: `${auditActionLabel(action)} · ${action}`,
})));

const load = async () => {
  loading.value = true;
  try {
    const response = await listAuditLogs({
      action: filters.action || undefined,
      keyword: filters.keyword.trim() || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined,
      limit: pageSize.value,
      offset: (currentPage.value - 1) * pageSize.value,
    });
    items.value = response.data.items;
    total.value = response.data.total;
    actions.value = response.data.actions;
  } catch (error: unknown) {
    const apiError = error as { response?: { data?: { error?: { message?: string } } } };
    message.error(apiError.response?.data?.error?.message || '加载审计日志失败');
  } finally {
    loading.value = false;
  }
};

const applyFilters = () => {
  currentPage.value = 1;
  void load();
};

const resetFilters = () => {
  filters.action = undefined;
  filters.keyword = '';
  filters.from = '';
  filters.to = '';
  currentPage.value = 1;
  void load();
};

const onPageChange = (page: number) => {
  currentPage.value = page;
  void load();
};

const onPageSizeChange = (_page: number, size: number) => {
  pageSize.value = size;
  currentPage.value = 1;
  void load();
};

const openDetail = (item: AuditLogItem) => {
  selected.value = item;
  detailOpen.value = true;
};

const formatDateTime = (value: string) => new Date(value).toLocaleString('zh-CN', { hour12: false });

onMounted(load);
</script>

<style scoped>
.audit-page{max-width:1440px;margin:0 auto}.heading{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:16px}.heading h1{margin:0;color:#14263a}.heading p{margin:4px 0 0;color:#7b8797}.filter-card{margin-bottom:12px}.filters{display:flex;flex-wrap:wrap;gap:10px;align-items:center}.date-input{height:32px;padding:0 10px;border:1px solid #d9d9d9;border-radius:6px;background:#fff;color:#27364a}.date-separator{color:#8c8c8c}.summary-row{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-bottom:12px}.action-code{margin-top:3px;color:#9aa4b2;font-size:10px;font-family:ui-monospace,Menlo,monospace}.mobile-list{display:none}.pagination-wrap{display:flex;justify-content:flex-end;margin-top:16px}.payload-json{max-height:calc(100vh - 320px);margin:0;overflow:auto;padding:16px;border-radius:8px;background:#0f1f2e;color:#d7e7f4;font:12px/1.65 ui-monospace,SFMono-Regular,Menlo,monospace;white-space:pre-wrap;overflow-wrap:anywhere}.audit-card{margin-bottom:10px}.audit-card-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;color:#8994a3;font-size:11px}.audit-card-foot{margin-top:8px;color:#8994a3;font-size:11px}@media(max-width:767px){.heading{align-items:flex-start;gap:10px}.filters>*{width:100%!important}.date-separator{display:none}.summary-row{grid-template-columns:repeat(3,1fr);gap:6px}.summary-row :deep(.ant-card-body){padding:10px}.desktop-table-card{display:none}.mobile-list{display:block}.pagination-wrap{justify-content:center;overflow-x:auto}.payload-json{max-height:none}.audit-page{width:100%}}
</style>
